import type { PinKey, PinState, PinVault } from '../../application/ports.ts'
import type { MisionHogarDb, SecretRow } from '../dexie/db.ts'

/**
 * PINs, stored as PBKDF2-SHA256 hashes and never as themselves.
 *
 * They live outside the event log on purpose: an exported backup is a file that
 * may be copied around, and it has no business carrying the credentials that
 * guard the house. The log records only *that* a PIN changed.
 *
 * There is more than one now. `pin` is the parent mode; `child:<id>` is one
 * child's own screen, which is optional and which a parent sets and clears.
 */

const ITERATIONS = 310_000
const SALT_BYTES = 16
const KEY_BITS = 256

const encoder = new TextEncoder()

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<string> {
  const material = await globalThis.crypto.subtle.importKey(
    'raw',
    encoder.encode(pin.normalize('NFKC')),
    'PBKDF2',
    false,
    ['deriveBits'],
  )
  const bits = await globalThis.crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    material,
    KEY_BITS,
  )
  return toBase64(new Uint8Array(bits))
}

/** Constant-time comparison, so a wrong PIN takes as long as a right one. */
function equalStrings(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return diff === 0
}

export class WebCryptoPinVault implements PinVault {
  private readonly db: MisionHogarDb
  private readonly now: () => Date
  private readonly iterations: number

  constructor(db: MisionHogarDb, now: () => Date, iterations: number = ITERATIONS) {
    this.db = db
    this.now = now
    this.iterations = iterations
  }

  private async row(key: PinKey): Promise<SecretRow | undefined> {
    return this.db.secrets.get(key)
  }

  async state(key: PinKey = 'pin'): Promise<PinState> {
    const row = await this.row(key)
    if (!row) return { isSet: false, consecutiveFailures: 0, lastFailureAt: null }
    return {
      isSet: true,
      consecutiveFailures: row.consecutiveFailures,
      lastFailureAt: row.lastFailureAt === null ? null : new Date(row.lastFailureAt),
    }
  }

  async set(pin: string, key: PinKey = 'pin'): Promise<void> {
    if (!/^\d{4,8}$/.test(pin)) {
      throw new RangeError('The PIN must be between four and eight digits.')
    }
    const salt = globalThis.crypto.getRandomValues(new Uint8Array(SALT_BYTES))
    const hash = await derive(pin, salt, this.iterations)
    await this.db.secrets.put({
      key,
      saltBase64: toBase64(salt),
      hashBase64: hash,
      iterations: this.iterations,
      consecutiveFailures: 0,
      lastFailureAt: null,
      updatedAt: this.now().toISOString(),
    })
  }

  async verify(pin: string, key: PinKey = 'pin'): Promise<boolean> {
    const row = await this.row(key)
    if (!row) return false
    const candidate = await derive(pin, fromBase64(row.saltBase64), row.iterations)
    const ok = equalStrings(candidate, row.hashBase64)
    await this.db.secrets.put({
      ...row,
      consecutiveFailures: ok ? 0 : row.consecutiveFailures + 1,
      lastFailureAt: ok ? null : this.now().toISOString(),
    })
    return ok
  }

  async clearFailures(key: PinKey = 'pin'): Promise<void> {
    const row = await this.row(key)
    if (!row) return
    await this.db.secrets.put({ ...row, consecutiveFailures: 0, lastFailureAt: null })
  }

  async remove(key: PinKey): Promise<void> {
    await this.db.secrets.delete(key)
  }
}
